// apps/admin/src/features/billing/ModeSettlementDialog.tsx
//
// Phase 8.2 Wave 4 Task 27 — ModeSettlementDialog (3-option modal).
//
// vg-binding: CONTEXT:decisions=P8.D-485,P4.1.D-29
// vg-binding: PLAN:task-27
//
// Per task-27 acceptance: shown when switching mode with active credit_memos.
// Three options:
//   - convert                       → settle now (credits → wallet)
//   - apply_credits_then_switch     → carry forward (credits → outstanding)
//   - refund_bank                   → refund to merchant bank
// Plus a passive "cancel" / "keep current mode" choice.

import type { JSX } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

export type SettlementAction =
  | 'convert'
  | 'refund_bank'
  | 'apply_credits_then_switch';

export interface CreditMemoSummary {
  id: string;
  amount_usd_cents: number;
}

export interface ModeSettlementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingMode: 'PREPAID' | 'CREDIT' | 'INVOICE' | null;
  settlementAction: SettlementAction | null;
  onActionChange: (next: SettlementAction | null) => void;
  activeCreditMemos: CreditMemoSummary[];
  creditMemoTotalCents: number;
  grossOutstandingCents: number;
  remainingAfterCreditsCents: number;
  insufficientCredit: boolean;
  modeSubmitting: boolean;
  onConfirm: () => void;
  formatUsdCents: (cents: number | null | undefined) => string;
}

export function ModeSettlementDialog(
  props: ModeSettlementDialogProps,
): JSX.Element {
  const { t } = useTranslation('finance');
  const {
    open,
    onOpenChange,
    pendingMode,
    settlementAction,
    onActionChange,
    activeCreditMemos,
    creditMemoTotalCents,
    grossOutstandingCents,
    remainingAfterCreditsCents,
    insufficientCredit,
    modeSubmitting,
    onConfirm,
    formatUsdCents,
  } = props;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onOpenChange(false);
          onActionChange(null);
        }
      }}
      className="max-w-2xl"
      data-testid="mode-settlement-dialog"
    >
      <DialogHeader>
        <DialogTitle>{t('merchant_billing.settlement.title')}</DialogTitle>
      </DialogHeader>
      <div className="mt-4 flex flex-col gap-4">
        <div className="rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-pw-text-muted">
              {t('merchant_billing.settlement.outstandingGross')}
            </span>
            <span className="font-semibold text-pw-text">
              {formatUsdCents(grossOutstandingCents)}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between gap-3">
            <span className="text-pw-text-muted">
              {t('merchant_billing.settlement.creditsAvailable')}
            </span>
            <span className="font-semibold text-pw-text">
              {formatUsdCents(creditMemoTotalCents)}
            </span>
          </div>
        </div>

        {/* Active credit memos preview list */}
        {activeCreditMemos.length > 0 ? (
          <div className="rounded-md border border-pw-border">
            <div className="border-b border-pw-border px-3 py-2 text-xs font-medium uppercase tracking-wide text-pw-text-muted">
              {t('merchant_billing.settlement.activeMemos', {
                count: activeCreditMemos.length,
              })}
            </div>
            <ul className="max-h-40 divide-y divide-pw-border overflow-auto">
              {activeCreditMemos.map((cm) => (
                <li
                  key={cm.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                >
                  <span className="font-mono text-pw-text-muted">
                    {cm.id.slice(0, 8)}…
                  </span>
                  <span className="font-medium text-pw-text">
                    {formatUsdCents(cm.amount_usd_cents)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* 3 settlement options — task-27 acceptance:
            1. Settle now    → convert credits to wallet
            2. Carry forward → apply credits to outstanding then switch
            3. Refund        → refund credits to bank
            Plus passive cancel option. */}
        <div className="flex flex-col gap-2">
          <label
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
              settlementAction === 'convert'
                ? 'border-pw-primary bg-pw-primary/5'
                : 'border-pw-border bg-pw-bg hover:bg-pw-surface',
            )}
          >
            <input
              type="radio"
              name="settlement-action"
              checked={settlementAction === 'convert'}
              onChange={() => onActionChange('convert')}
              className="mt-0.5 h-4 w-4 accent-pw-primary"
              data-testid="settlement-action-convert-btn"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-pw-text">
                {t('merchant_billing.settlement.convertTitle')}
              </div>
              <div className="mt-0.5 text-xs text-pw-text-muted">
                {t('merchant_billing.settlement.convertDesc', {
                  amount: formatUsdCents(creditMemoTotalCents),
                })}
              </div>
            </div>
          </label>

          <label
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
              settlementAction === 'apply_credits_then_switch'
                ? 'border-pw-primary bg-pw-primary/5'
                : 'border-pw-border bg-pw-bg hover:bg-pw-surface',
            )}
          >
            <input
              type="radio"
              name="settlement-action"
              checked={settlementAction === 'apply_credits_then_switch'}
              onChange={() => onActionChange('apply_credits_then_switch')}
              className="mt-0.5 h-4 w-4 accent-pw-primary"
              data-testid="settlement-action-apply-credits-btn"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-pw-text">
                {t('merchant_billing.settlement.carryTitle')}
              </div>
              <div className="mt-0.5 text-xs text-pw-text-muted">
                {t('merchant_billing.settlement.carryDesc', {
                  amount: formatUsdCents(creditMemoTotalCents),
                })}
              </div>
              {settlementAction === 'apply_credits_then_switch' &&
              insufficientCredit ? (
                <p className="mt-2 rounded-md bg-rose-50 dark:bg-rose-950/40 px-2 py-1 text-xs text-rose-700 dark:text-rose-300">
                  {t('merchant_billing.settlement.insufficientInline', {
                    amount: formatUsdCents(remainingAfterCreditsCents),
                  })}
                </p>
              ) : null}
            </div>
          </label>

          <label
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
              settlementAction === 'refund_bank'
                ? 'border-pw-primary bg-pw-primary/5'
                : 'border-pw-border bg-pw-bg hover:bg-pw-surface',
            )}
          >
            <input
              type="radio"
              name="settlement-action"
              checked={settlementAction === 'refund_bank'}
              onChange={() => onActionChange('refund_bank')}
              className="mt-0.5 h-4 w-4 accent-pw-primary"
              data-testid="settlement-action-refund-bank-btn"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-pw-text">
                {t('merchant_billing.settlement.refundTitle')}
              </div>
              <div className="mt-0.5 text-xs text-pw-text-muted">
                {t('merchant_billing.settlement.refundDesc', {
                  amount: formatUsdCents(creditMemoTotalCents),
                })}
              </div>
            </div>
          </label>

          <label
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
              settlementAction === null
                ? 'border-pw-border bg-pw-surface'
                : 'border-pw-border bg-pw-bg hover:bg-pw-surface',
            )}
          >
            <input
              type="radio"
              name="settlement-action"
              checked={settlementAction === null}
              onChange={() => onActionChange(null)}
              className="mt-0.5 h-4 w-4 accent-pw-primary"
              data-testid="settlement-action-cancel-btn"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-pw-text">
                {t('merchant_billing.settlement.cancelTitle')}
              </div>
              <div className="mt-0.5 text-xs text-pw-text-muted">
                {t('merchant_billing.settlement.cancelDesc')}
              </div>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
              onActionChange(null);
            }}
          >
            {t('merchant_billing.settlement.close')}
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={
              modeSubmitting ||
              settlementAction === null ||
              (settlementAction === 'apply_credits_then_switch' &&
                insufficientCredit)
            }
            aria-disabled={
              modeSubmitting ||
              settlementAction === null ||
              (settlementAction === 'apply_credits_then_switch' &&
                insufficientCredit)
            }
            onClick={onConfirm}
            data-testid="settlement-action-confirm-btn"
          >
            {modeSubmitting
              ? t('merchant_billing.mode.switching')
              : t('merchant_billing.settlement.confirm', {
                  mode: pendingMode ?? '',
                })}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}

export default ModeSettlementDialog;
